import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, TrendingDown, Zap, ArrowRight, Sofa, HardHat, TreePine, ShoppingBag, CheckCircle, Truck } from 'lucide-react';
import { PageHeader, PageFooter } from './CustomersPage';

export default function BusinessesPage() {
  const navigate = useNavigate();

  const points = [
    { icon: Users, title: 'Access a Network of Drivers', desc: 'Tap into a pool of local, independent drivers ready to deliver on demand.' },
    { icon: TrendingDown, title: 'Reduce Delivery Costs', desc: 'No fleet, no wages, no insurance overhead. Pay per delivery, only when you need it.' },
    { icon: Zap, title: 'Scale Instantly', desc: 'One delivery or fifty — scale up and down as your business demands.' },
  ];

  const useCases = [
    { icon: Sofa, label: 'Furniture Stores', desc: 'Deliver sofas, beds, and tables without owning a van fleet.' },
    { icon: HardHat, label: 'Building Suppliers', desc: 'Get materials to sites quickly with on-demand drivers.' },
    { icon: TreePine, label: 'Garden Centres', desc: 'Deliver plants, pots, and landscaping supplies to customers.' },
    { icon: ShoppingBag, label: 'Retail & Marketplace', desc: 'Offer delivery on large items sold online or in-store.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="businesses-page">
      <PageHeader />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-blue-50/50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1565C0]">For Businesses</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
            Reliable Delivery<br /><span className="text-[#1565C0]">Without the Overhead</span>
          </h1>
          <p className="mt-5 text-lg text-gray-600 max-w-2xl mx-auto">
            Outsource your deliveries to a network of independent drivers. No vans to maintain, no staff to manage — just post jobs and let drivers handle the rest.
          </p>
          <button onClick={() => navigate('/register')} className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#1565C0] text-white font-bold text-base hover:bg-[#0D47A1] transition-all active:scale-95 rounded-md" data-testid="businesses-cta">
            <Building2 className="w-5 h-5" /> Create a Business Account <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900">Why Businesses Use JustDeliver</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {points.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:-translate-y-0.5 transition-transform">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
                  <Icon className="w-6 h-6 text-[#1565C0]" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 text-base font-bold text-gray-900">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900">Built For</h2>
          <p className="text-center text-gray-600 mt-2">Businesses that need reliable last-mile delivery</p>
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {useCases.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#1565C0]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{label}</h3>
                  <p className="mt-1 text-sm text-gray-600">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Simple Setup</h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            {[
              { num: '1', text: 'Create a business account' },
              { num: '2', text: 'Post delivery jobs' },
              { num: '3', text: 'Drivers bid & deliver' },
            ].map(({ num, text }, i) => (
              <React.Fragment key={num}>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#1565C0] text-white flex items-center justify-center font-bold">{num}</span>
                  <span className="text-sm font-medium text-gray-700">{text}</span>
                </div>
                {i < 2 && <ArrowRight className="w-4 h-4 text-gray-300 hidden sm:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 bg-[#1565C0] text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold">Start Delivering Smarter</h2>
          <p className="mt-3 text-blue-100">No contracts. No fleet costs. Just reliable delivery when you need it.</p>
          <button onClick={() => navigate('/register')} className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1565C0] font-bold hover:bg-gray-100 transition-all active:scale-95 rounded-md">
            <Building2 className="w-5 h-5" /> Create a Business Account
          </button>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
